import {
  resetCreateUsuarioFormState,
  resetEditUsuarioFormState,
  resetUsuariosState,
  setClienteSelecionadoState,
  setCreateUsuarioFormIdClienteState,
  setCreateUsuarioFormTipoUsuarioState,
  setCreateUsuarioFormEmailState,
  setCreateUsuarioFormErrorsState,
  setCreateUsuarioFormLoginState,
  setCreateUsuarioFormNomeState,
  setCreateUsuarioFormIdPerfilState,
  setCreateUsuarioFormTouchedState,
  setEditUsuarioFormIdClienteState,
  setEditUsuarioFormEmailState,
  setEditUsuarioFormErrorsState,
  setEditUsuarioFormIdUsuarioState,
  setEditUsuarioFormNomeState,
  setEditUsuarioFormAtivoState,
  setEditUsuarioFormTouchedState,
  setResetClienteSelecionadoState,
  setUsuariosPaginationState,
  setUsuariosState, setUsuariosTotalState,
  validateCreateUsuarioForm,
  validateEditUsuarioForm,
  setEditUsuarioFormLoginState,
  setEditUsuarioFormTipoUsuarioState,
  setEditUsuarioFormIdPerfilState,
  setEditUsuarioFormSenhaState,
  setCreateUsuarioFormTokenAtivoState,
  setEditUsuarioFormTokenState,
  setEditUsuarioFormTokenAtivoState,
  setEditUsuarioFormIdsClientesState,
  setEditUsuarioFormInitialIdsClientesState,
  setClientesVendedorVelesState,
  setEditUsuarioFormBuscaDisponiveisState,
  setEditUsuarioFormBuscaSelecionadosState
} from "../../application/states/usuarios"
import { useCallback } from "react"
import { useAppDispatch, useAppSelector } from "../../presentation/hooks/store-hooks"
import { useUserApi } from "../../infra/user"
import { useClienteApi } from "../../infra/cliente"
import { useCryptoApi } from "../../infra/crypto/useCryptoApi"
import { useUIService } from "../ui"
import { useNavigate } from "react-router-dom"
import { ICliente, ITipoUsuario } from "../../application/models"
import { usePerfilService } from "../perfil"

export const useUsuariosService = () => {
  // Dispatch
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  // State
  const { currentUser } = useAppSelector((state) => state.user)
  const {
    createUsuarioForm,
    editUsuarioForm,
    clienteSelecionado
  } = useAppSelector((state) => state.usuarios)
  // API
  const {
    findUsuariosByIdClienteApi,
    createUserApi,
    updateUserApi,
    sendRecoveryPasswordEmailApi,
    getUserByIdApi,
    generateTokenApi,
    getClientesVelesSelecionadosVizualizacaoApi
  } = useUserApi()
  const { decryptApi } = useCryptoApi()
  // Service
  const { setAlert, setLoading } = useUIService()
  const { getPerfisUsuario } = usePerfilService()

  // Lists Actions
  const setUsuarios = useCallback(async (data?: { idCliente: number }) => {
    const idCliente = data?.idCliente ?? clienteSelecionado?.IdCliente
    if (idCliente) {
      const loadingId = Math.random().toString()

      setLoading({
        loading: true,
        id: loadingId,
        message: 'Buscando usuários...'
      })

      try {
        const usuarios = await findUsuariosByIdClienteApi({
          idCliente,
          limit: 1000,
          page: 1
        })

        dispatch(setUsuariosState({
          usuarios: usuarios.list
        }))
        dispatch(setUsuariosTotalState({
          total: usuarios.total
        }))
      } catch (error: any) {
        setAlert({
          message: error.response?.data?.error || error.response?.data?.message || 'Erro ao buscar usuários',
          type: 'error'
        })
      } finally {
        setLoading({
          loading: false,
          id: loadingId
        })
      }
    }
  }, [clienteSelecionado, dispatch, findUsuariosByIdClienteApi, setAlert, setLoading])

  const setUsuariosPagination = useCallback((data: {
    page: number,
    limit: number
  }) => {
    dispatch(setUsuariosPaginationState({
      page: data.page,
      limit: data.limit
    }))
  }, [dispatch])

  //Create Usuario Actions
  const setOpenCreateUsuarioForm = useCallback((data: {
    openForm: boolean
  }) => {
    if (data.openForm) {
      if (currentUser) {
        if (clienteSelecionado) {
          dispatch(setCreateUsuarioFormIdClienteState({
            idCliente: clienteSelecionado.IdCliente
          }))

          navigate('/dashboard/cadastro/usuario/create')
        }
      }
    } else {
      dispatch(resetCreateUsuarioFormState())
      navigate('/dashboard/cadastro/usuario')
    }
  }, [clienteSelecionado, currentUser, dispatch, navigate])

  const setCreateUsuarioFormNome = useCallback((data: {
    nome: string
  }) => {
    dispatch(setCreateUsuarioFormNomeState({
      nome: data.nome
    }))
  }, [dispatch])

  const setCreateUsuarioFormEmail = useCallback((data: {
    email: string
  }) => {
    dispatch(setCreateUsuarioFormEmailState({
      email: data.email
    }))
  }, [dispatch])

  const setCreateUsuarioFormLogin = useCallback((data: {
    login: string
  }) => {
    dispatch(setCreateUsuarioFormLoginState({
      login: data.login
    }))
  }, [dispatch])

  const setCreateUsuarioFormIdPerfil = useCallback((data: {
    idPerfil: number
  }) => {
    dispatch(setCreateUsuarioFormIdPerfilState({
      idPerfil: data.idPerfil
    }))
  }, [dispatch])

  const setCreateUsuarioFormTipoUsuario = useCallback((data: {
    tipoUsuario: ITipoUsuario
  }) => {
    dispatch(setCreateUsuarioFormTipoUsuarioState({
      tipoUsuario: data.tipoUsuario
    }))
  }, [dispatch])

  const setCreateUsuarioFormTokenAtivo = useCallback((data: {
    tokenAtivo: boolean
  }) => {
    dispatch(setCreateUsuarioFormTokenAtivoState({
      tokenAtivo: data.tokenAtivo
    }))
  }, [dispatch])

  const createUsuario = useCallback(async () => {
    const loadingId = Math.random().toString()

    try {
      const errors = validateCreateUsuarioForm(createUsuarioForm.fields)
      if (errors.length > 0) {
        dispatch(setCreateUsuarioFormErrorsState({
          errors: errors
        }))
        dispatch(setCreateUsuarioFormTouchedState({
          touched: true
        }))
      } else {
        setLoading({
          loading: true,
          id: loadingId,
          message: 'Criando usuário...'
        })

        await createUserApi({
          nome: createUsuarioForm.fields.nome,
          email: createUsuarioForm.fields.email,
          login: createUsuarioForm.fields.login,
          idCliente: createUsuarioForm.fields.idCliente,
          idPerfil: createUsuarioForm.fields.idPerfil,
          tipoUsuario: createUsuarioForm.fields.tipoUsuario ?? ITipoUsuario.USUARIO_CLIENTE,  //Se não tiver nenhum selecionado é Cliente.
          tokenAtivo: createUsuarioForm.fields.tokenAtivo,
          idsClientes: createUsuarioForm.fields.idsClientes
        })

        dispatch(resetCreateUsuarioFormState())

        setOpenCreateUsuarioForm({
          openForm: false
        })

        setAlert({
          message: 'Usuário criado com sucesso',
          type: 'success'
        })
      }
    } catch (error: any) {
      setAlert({
        message: error.response.data.message || error.response.data.error,
        type: 'error'
      })
    } finally {
      setLoading({
        loading: false,
        id: loadingId
      })
    }

  }, [setLoading, createUsuarioForm.fields, dispatch, createUserApi, setOpenCreateUsuarioForm, setAlert])

  // Edit Usuario Actions
  const setOpenEditUsuarioForm = useCallback(async (data: {
    open: boolean,
    idUsuario?: number
    nome?: string
    email?: string
    ativo?: boolean
    login?: string
    tipoUsuario?: ITipoUsuario | null
    idPerfil?: number | null
  }) => {
    if (data.open) {
      dispatch(setEditUsuarioFormIdUsuarioState({
        idUsuario: data.idUsuario!
      }))

      dispatch(setEditUsuarioFormNomeState({
        nome: data.nome!
      }))

      dispatch(setEditUsuarioFormEmailState({
        email: data.email!
      }))

      dispatch(setEditUsuarioFormAtivoState({
        ativo: data.ativo!
      }))

      dispatch(setEditUsuarioFormLoginState({
        login: data.login!
      }))

      if (data.idPerfil) {
        dispatch(setEditUsuarioFormIdPerfilState({
          idPerfil: data.idPerfil
        }))
      }

      if (data.tipoUsuario) {
        dispatch(setEditUsuarioFormTipoUsuarioState({
          tipoUsuario: data.tipoUsuario
        }))
      }

      if (clienteSelecionado) {
        dispatch(setEditUsuarioFormIdClienteState({
          idCliente: clienteSelecionado.IdCliente
        }))
      }

      dispatch(setEditUsuarioFormIdsClientesState({
        idsClientes: []
      }))
      dispatch(setEditUsuarioFormInitialIdsClientesState({
        initialIdsClientes: []
      }))
      dispatch(setEditUsuarioFormBuscaDisponiveisState({
        buscaDisponiveis: ''
      }))
      dispatch(setEditUsuarioFormBuscaSelecionadosState({
        buscaSelecionados: ''
      }))

      navigate('/dashboard/cadastro/usuario/edit')
    } else {
      navigate('/dashboard/cadastro/usuario')
      dispatch(resetEditUsuarioFormState())
    }
  }, [clienteSelecionado, dispatch, navigate])

  const setEditUsuarioFormTipoUsuario = useCallback((data: {
    tipoUsuario: ITipoUsuario
  }) => {
    dispatch(setEditUsuarioFormTipoUsuarioState({
      tipoUsuario: data.tipoUsuario
    }))
  }, [dispatch])

  const setEditUsuarioFormNome = useCallback((data: {
    nome: string
  }) => {
    dispatch(setEditUsuarioFormNomeState({
      nome: data.nome
    }))
  }, [dispatch])

  const setEditUsuarioFormEmail = useCallback((data: {
    email: string
  }) => {
    dispatch(setEditUsuarioFormEmailState({
      email: data.email
    }))
  }, [dispatch])

  const setEditUsuarioFormLogin = useCallback((data: {
    login: string
  }) => {
    dispatch(setEditUsuarioFormLoginState({
      login: data.login
    }))
  }, [dispatch])

  const setEditUsuarioFormIdPerfil = useCallback((data: {
    idPerfil: number
  }) => {
    dispatch(setEditUsuarioFormIdPerfilState({
      idPerfil: data.idPerfil
    }))
  }, [dispatch])

  const setEditUsuarioFormAtivo = useCallback((data: {
    ativo: boolean
  }) => {
    dispatch(setEditUsuarioFormAtivoState({
      ativo: data.ativo
    }))
  }, [dispatch])

  const setEditUsuarioFormSenha = useCallback((data: {
    senha: string
  }) => {
    dispatch(setEditUsuarioFormSenhaState({
      senha: data.senha
    }))
  }, [dispatch])

  const setEditUsuarioFormToken = useCallback((data: {
    token: string
  }) => {
    dispatch(setEditUsuarioFormTokenState({
      token: data.token
    }))
  }, [dispatch])

  const setEditUsuarioFormTokenAtivo = useCallback((data: {
    tokenAtivo: boolean
  }) => {
    dispatch(setEditUsuarioFormTokenAtivoState({
      tokenAtivo: data.tokenAtivo
    }))
  }, [dispatch])

  const updateUsuario = useCallback(async () => {
    const loadingId = Math.random().toString()

    try {
      const errors = validateEditUsuarioForm(editUsuarioForm.fields)
      if (errors.length > 0) {
        dispatch(setEditUsuarioFormErrorsState({
          errors: errors
        }))
        dispatch(setEditUsuarioFormTouchedState({
          touched: true
        }))
      } else {
        setLoading({
          loading: true,
          id: loadingId,
          message: 'Atualizando usuário...'
        })

        const currentIds = editUsuarioForm.fields.idsClientes || []
        const initialIds = editUsuarioForm.fields.initialIdsClientes || []

        const clientesVisualizacao: { IdCliente: number; Ativo: boolean }[] = [
          ...currentIds.map(id => ({ IdCliente: id, Ativo: true })),
          ...initialIds.filter(id => !currentIds.includes(id)).map(id => ({ IdCliente: id, Ativo: false }))
        ]

        const result = await updateUserApi({
          ativo: editUsuarioForm.fields.ativo,
          email: editUsuarioForm.fields.email,
          login: editUsuarioForm.fields.login,
          idCliente: editUsuarioForm.fields.idCliente,
          idPerfil: editUsuarioForm.fields.idPerfil!,
          idUsuario: editUsuarioForm.fields.idUsuario,
          nome: editUsuarioForm.fields.nome,
          tipoUsuario: editUsuarioForm.fields.tipoUsuario ?? ITipoUsuario.USUARIO_CLIENTE,
          token: editUsuarioForm.fields.token,
          tokenAtivo: editUsuarioForm.fields.tokenAtivo,
          idsClientes: clientesVisualizacao
        })

        dispatch(resetEditUsuarioFormState())

        setAlert({
          message: 'Usuário atualizado com sucesso',
          type: 'success'
        })

        // Recarregar a lista de usuários após atualização
        if (clienteSelecionado) {
          await setUsuarios()
        }

        navigate('/dashboard/cadastro/usuario')
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Erro ao atualizar usuário'

      setAlert({
        message: errorMessage,
        type: 'error'
      })
    } finally {
      setLoading({
        loading: false,
        id: loadingId
      })
    }
  }, [editUsuarioForm.fields, dispatch, setLoading, updateUserApi, setAlert, navigate, clienteSelecionado, setUsuarios])

  // Change Usuario Password Actions
  const setClienteSelecionado = useCallback(async (data: {
    cliente: ICliente
  }) => {
    dispatch(setClienteSelecionadoState({
      cliente: data.cliente
    }))

    getPerfisUsuario({
      idCliente: data.cliente.IdCliente,
      limit: 1000
    })
  }, [dispatch, getPerfisUsuario])

  const setResetClienteSelecionado = useCallback(() => {
    dispatch(setResetClienteSelecionadoState())
  }, [dispatch])

  const resetUsuarios = useCallback(() => {
    dispatch(resetUsuariosState())
  }, [dispatch])

  // Send Recovery Password Email
  const sendRecoveryPasswordEmail = useCallback(async (idUsuario: number) => {
    const loadingId = Math.random().toString()
    try {
      setLoading({
        loading: true,
        id: loadingId,
        message: 'Enviando email de recuperação de senha...'
      })

      await sendRecoveryPasswordEmailApi({
        idUsuario
      })

      setAlert({
        message: 'Email de recuperação de senha enviado com sucesso!',
        type: 'success'
      })
    } catch (error: any) {
      setAlert({
        message: error.response?.data?.error || error.response?.data?.message || 'Erro ao enviar email de recuperação de senha',
        type: 'error'
      })
    } finally {
      setLoading({
        loading: false,
        id: loadingId
      })
    }
  }, [sendRecoveryPasswordEmailApi, setAlert, setLoading])

  // Get Usuario By Id and update form
  const getUsuarioByIdAndUpdateForm = useCallback(async (idUsuario: number) => {
    try {
      const usuarioData = await getUserByIdApi({
        idUsuario
      })

      if (usuarioData.usuario) {
        // Atualizar formulário com dados atualizados
        dispatch(setEditUsuarioFormNomeState({
          nome: usuarioData.usuario.Nome
        }))
        dispatch(setEditUsuarioFormEmailState({
          email: usuarioData.usuario.Email
        }))
        dispatch(setEditUsuarioFormAtivoState({
          ativo: usuarioData.usuario.Ativo
        }))
        if (usuarioData.usuario.IdPerfil) {
          dispatch(setEditUsuarioFormIdPerfilState({
            idPerfil: usuarioData.usuario.IdPerfil
          }))
        }
        if (usuarioData.usuario.IdVelesPerfilInterno) {
          dispatch(setEditUsuarioFormTipoUsuarioState({
            tipoUsuario: usuarioData.usuario.IdVelesPerfilInterno
          }))
        }
        if (usuarioData.usuario.IdCliente) {
          dispatch(setEditUsuarioFormIdClienteState({
            idCliente: usuarioData.usuario.IdCliente
          }))
          getPerfisUsuario({
            idCliente: usuarioData.usuario.IdCliente,
            limit: 1000
          })
        }

        // Atualizar token se existir
        if (usuarioData.usuario.Token !== undefined && usuarioData.usuario.Token !== null) {
          dispatch(setEditUsuarioFormTokenState({
            token: usuarioData.usuario.Token
          }))
        }
        // Atualizar tokenAtivo se existir
        if (usuarioData.usuario.TokenAtivo !== undefined) {
          dispatch(setEditUsuarioFormTokenAtivoState({
            tokenAtivo: usuarioData.usuario.TokenAtivo
          }))
        }
        // Resetar campos de clientes selecionados antes de carregar os do novo usuário
        dispatch(setEditUsuarioFormIdsClientesState({
          idsClientes: []
        }))
        dispatch(setEditUsuarioFormInitialIdsClientesState({
          initialIdsClientes: []
        }))
        dispatch(setEditUsuarioFormBuscaDisponiveisState({
          buscaDisponiveis: ''
        }))
        dispatch(setEditUsuarioFormBuscaSelecionadosState({
          buscaSelecionados: ''
        }))

        try {
          const vizData = await getClientesVelesSelecionadosVizualizacaoApi({ idUsuario })
          if (vizData && vizData.idsClientes) {
            dispatch(setEditUsuarioFormIdsClientesState({
              idsClientes: vizData.idsClientes
            }))
            dispatch(setEditUsuarioFormInitialIdsClientesState({
              initialIdsClientes: vizData.idsClientes
            }))
          }
        } catch (vizError) {
          throw vizError
        }
      }

      return usuarioData
    } catch (error: any) {
      console.error('Erro ao carregar dados do usuário:', error)
      throw error
    }
  }, [getUserByIdApi, getClientesVelesSelecionadosVizualizacaoApi, dispatch, getPerfisUsuario])


  // Get Senha Descriptografada
  const getSenhaDescriptografada = useCallback(async (idUsuario: number): Promise<string> => {
    try {
      // Busca o usuário completo (agora retorna a senha criptografada)
      const usuarioData = await getUserByIdApi({
        idUsuario
      })

      // Verifica se o usuário tem senha criptografada
      if (usuarioData.usuario && usuarioData.usuario.Senha) {
        const senhaCriptografada = usuarioData.usuario.Senha

        // Descriptografa a senha usando o endpoint de decrypt
        const senhaDescriptografada = await decryptApi({
          encrypted: senhaCriptografada
        })

        return senhaDescriptografada
      } else {
        return ''
      }
    } catch (error: any) {
      console.error('Erro ao carregar senha:', error)
      throw error
    }
  }, [getUserByIdApi, decryptApi])

  // Generate Token
  const generateToken = useCallback(async (): Promise<string> => {
    const loadingId = Math.random().toString()
    try {
      setLoading({
        loading: true,
        id: loadingId,
        message: 'Gerando token...'
      })

      const result = await generateTokenApi()

      // Atualizar formulário com o token gerado
      dispatch(setEditUsuarioFormTokenState({
        token: result.token
      }))
      dispatch(setEditUsuarioFormTokenAtivoState({
        tokenAtivo: true
      }))

      setAlert({
        message: 'Token gerado com sucesso!',
        type: 'success'
      })

      return result.token
    } catch (error: any) {
      setAlert({
        message: error.response?.data?.error || error.response?.data?.message || 'Erro ao gerar token',
        type: 'error'
      })
      throw error
    } finally {
      setLoading({
        loading: false,
        id: loadingId
      })
    }
  }, [generateTokenApi, dispatch, setAlert, setLoading])

  const { getClientesVendedorVelesApi } = useClienteApi()

  // Get Clientes Vendedor Veles
  const getClientesVendedorVeles = useCallback(async (idUsuario?: number) => {
    try {
      const res = await getClientesVendedorVelesApi(idUsuario)
      const clientes = Array.isArray(res) ? res : (res?.clientes || [])
      dispatch(setClientesVendedorVelesState({ clientes }))
      return clientes
    } catch (error: any) {
      throw error
    }
  }, [getClientesVendedorVelesApi, dispatch])

  return {
    setUsuarios,
    setUsuariosPagination,
    getClientesVendedorVeles,

    // Create Usuario Actions
    setOpenCreateUsuarioForm,
    setCreateUsuarioFormNome,
    setCreateUsuarioFormEmail,
    setCreateUsuarioFormLogin,
    setCreateUsuarioFormIdPerfil,
    setCreateUsuarioFormTipoUsuario,
    setCreateUsuarioFormTokenAtivo,
    createUsuario,
    // Edit Usuario Actions
    setOpenEditUsuarioForm,
    setEditUsuarioFormNome,
    setEditUsuarioFormEmail,
    setEditUsuarioFormLogin,
    setEditUsuarioFormTipoUsuario,
    setEditUsuarioFormIdPerfil,
    setEditUsuarioFormAtivo,
    setEditUsuarioFormToken,
    setEditUsuarioFormTokenAtivo,
    updateUsuario,
    setClienteSelecionado,
    setResetClienteSelecionado,
    resetUsuarios,
    sendRecoveryPasswordEmail,
    getUsuarioByIdAndUpdateForm,
    getSenhaDescriptografada,
    generateToken
  }
}
